import React, { useMemo, useState } from 'react';
import {
  ArrowDownLeft, Bank, BellRinging, Books, Buildings, CalendarBlank, ChartPieSlice, CheckCircle, Coins, CurrencyEur, Door, EnvelopeSimpleOpen, FileText,
  Check, Gavel, Hand, Info, Key, PencilSimple, PiggyBank, Plus, Scales, Signature, TrendUp, User, Users, WarningCircle, type Icon,
} from '@phosphor-icons/react';
import {
  FeldRaster, ImmoButton, InnenKarte, KartenTitel, KpiReihe, KpiTile, LeerZustand, PanelZeile, SegmentedControl, TextFeld, ToastPill, useToastPill,
} from '../../../../../components/immobilien/ui';
import { eur } from '../../../../../lib/immobilien/format';
import { deDatum } from '../../../../../lib/immobilien/objektDetail';
import { BUCHHALTUNG_FOLGT, BUCHHALTUNG_FOLGT_LANG } from '../../../../../lib/immobilien/hinweise';
import { leseZahl } from '../../../../../lib/immobilien/mietverhaeltnisFormular';
import { buchungsZeilen, eingangBalken } from '../../../../../lib/mieter/finanzen';
import { oeffneMieterDokument, type MieterDokument } from '../../../../../lib/mieter/mieterDokumente';
import { bucheZahlung } from '../../../../../lib/mietkonto/api';
import { eigentuemerKonto } from '../../../../../lib/mietkonto/typen';
import { meldeMietkontoGeaendert } from '../../../../../lib/mietkonto/useMietkonto';
import { VERWALTUNG_ABSENDER } from '../../../../../lib/rechnungen/rechnungsanschrift';
import { useHausgeldKonten } from '../../../../../lib/eigentuemer/useHausgeldKonten';
import { useWegDaten, type WegDokument } from '../../../../../lib/eigentuemer/useWegDaten';
import {
  bestellungFehler, bestellungStatus, einladungBis, istEtv, ruecklageWerte, stimmen, unterlagenArt, wegEinheiten, zahlweg, zusammensetzung, type WegEinheit,
} from '../../../../../lib/eigentuemer/weg';
import { artLabel, vollmachtGueltig } from '../../../../../lib/eigentuemer/beteiligte';
import { DatenZeile } from '../../../objekte/einheit/bereiche/teile';
import { aktualisiereObjekt } from '../../../../../lib/eigentuemer/aktionen';
import { meldeEigentuemerGeaendert } from '../../../../../lib/eigentuemer/useEigentuemerDetail';
import { useEigSeite, type EigSeitenWert } from '../EigentuemerSeitenKontext';

const GLAS: React.CSSProperties = { background: 'var(--glass)' };
const PIP: Record<string, string> = { ok: 'var(--ok)', warn: 'var(--warn)', bad: 'var(--bad)', vor: 'var(--fill2)' };
const tausend = (n: number) => n.toLocaleString('de-DE', { maximumFractionDigits: 2 });
const fehlerText = (e: unknown) => (e instanceof Error ? e.message : 'Unbekannter Fehler');

/** WEG-Einheiten dieses Eigentümers (stabil je Daten). */
function useWegEinheiten(s: EigSeitenWert): WegEinheit[] {
  return useMemo(() => wegEinheiten(s.daten.eigentuemer.id, s.daten.zeile.zuordnungen, s.daten.einheitEig, s.heute), [s.daten, s.heute]);
}

function Zeilen({ titel, icon, zeilen }: { titel: string; icon?: Icon; zeilen: { label: string; wert: React.ReactNode; zahl?: boolean }[] }) {
  return (
    <InnenKarte style={GLAS} gap={0}>
      <KartenTitel icon={icon}>{titel}</KartenTitel>
      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(340px, 100%), 1fr))', columnGap: 28 }}>
        {zeilen.map(z => <DatenZeile key={z.label} label={z.label} wert={z.wert} zahl={z.zahl} />)}
      </div>
    </InnenKarte>
  );
}

function Hinweis({ titel, children }: { titel: string; children: React.ReactNode }) {
  return (
    <InnenKarte style={GLAS}>
      <div className="flex items-start" style={{ gap: 12 }}>
        <span className="flex items-center justify-center shrink-0" style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--acc-soft)' }}>
          <Info weight="fill" size={18} color="var(--acc)" />
        </span>
        <div className="flex flex-col" style={{ gap: 4 }}>
          <span style={{ fontSize: 15, fontWeight: 800 }}>{titel}</span>
          <span style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--ink2)' }}>{children}</span>
        </div>
      </div>
    </InnenKarte>
  );
}

/** Dokumentzeile: antippen öffnet (Storage/Drive/OneDrive wie bei den Mietern). */
function DokZeile({ d, label, zeige }: { d: MieterDokument; label: string; zeige: (t: string, f?: boolean) => void }) {
  return (
    <PanelZeile icon={FileText} ton="acc" titel={d.titel || d.datei_name || 'Dokument'} sub={`${label} · ${deDatum(d.created_at.slice(0, 10))}`}
      onClick={() => { void oeffneMieterDokument(d).then(ok => { if (!ok) zeige('Dokument konnte nicht geöffnet werden', true); }); }} ariaLabel={`${d.titel || d.datei_name || 'Dokument'} öffnen`} />
  );
}

/** WEG-Unterlagen: Dokumente am Objekt und am Eigentümer, nach Art erkannt. */
function useUnterlagen(s: EigSeitenWert, objektIds: readonly string[], einheitIds: readonly string[]) {
  const weg = useWegDaten(objektIds, einheitIds, s.heute);
  const unterlagen = useMemo(() => {
    const alle: (WegDokument | MieterDokument)[] = [...(weg.status === 'ok' ? weg.daten.dokumente : []), ...s.daten.dokumente];
    const gesehen = new Set<string>();
    return alle.flatMap(d => {
      if (gesehen.has(d.id)) return [];
      gesehen.add(d.id);
      const a = unterlagenArt(d);
      return a ? [{ d, ...a }] : [];
    });
  }, [weg, s.daten.dokumente]);
  return { weg, unterlagen };
}

// ── Hausgeldkonto (Screens 20, 21, 65) ─────────────────────────────────────────
export function HausgeldPanel() {
  const s = useEigSeite();
  const w = s.daten.zeile.werte;
  const eig = s.daten.eigentuemer;
  const einheiten = useWegEinheiten(s);
  const kontoEinheiten = useMemo(() => einheiten.map(e => ({ id: e.id, objektId: e.objektId })), [einheiten]);
  const hk = useHausgeldKonten(eig.id, kontoEinheiten);
  const z = zusammensetzung(einheiten);
  const [toast, zeige] = useToastPill();
  const [einheitId, setEinheitId] = useState(einheiten[0]?.id ?? '');
  const [betrag, setBetrag] = useState('');
  const [datum, setDatum] = useState(s.heute);
  const [laeuft, setLaeuft] = useState(false);
  const bereit = hk.status === 'ok' && !!einheitId;
  const rueckstand = hk.status === 'ok' && hk.saldo < -0.5;

  const buche = async () => {
    const e = einheiten.find(x => x.id === einheitId);
    const b = leseZahl(betrag);
    if (!e || b === null || b <= 0) { zeige('Bitte einen Betrag größer als 0 eingeben.', true); return; }
    setLaeuft(true);
    try {
      await bucheZahlung({ objektId: e.objektId, kontoSchluessel: eigentuemerKonto(eig.id, e.id), einheitId: e.id, eigentuemerId: eig.id, zahlerName: s.name, datum, betrag: b, quelle: 'mietkonto' });
      meldeMietkontoGeaendert(e.objektId);
      setBetrag('');
      zeige(`Hausgeldzahlung über ${eur(b)} gebucht`);
    } catch (err) {
      zeige(`Nicht gebucht: ${fehlerText(err)}`, true);
    } finally {
      setLaeuft(false);
    }
  };
  const erinnerung = rueckstand && s.email
    ? `mailto:${s.email}?subject=${encodeURIComponent('Erinnerung: Hausgeld')}&body=${encodeURIComponent(`Guten Tag,\n\nauf Ihrem Hausgeldkonto ist ein Betrag von ${eur(-(hk.status === 'ok' ? hk.saldo : 0))} offen. Bitte überweisen Sie den Betrag zeitnah.\n\nMit freundlichen Grüßen`)}`
    : null;

  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <KpiReihe>
        <KpiTile layout="kachel" label="Hausgeld / Monat" wert={eur(w.hgM, { nachkomma: 0 })} icon={Coins} ton="acc" />
        <KpiTile layout="kachel" label="Saldo Hausgeld" icon={Scales}
          wert={hk.status === 'ok' ? (rueckstand ? eur(hk.saldo) : 'Ausgeglichen') : '–'} sub={hk.status === 'ok' ? undefined : BUCHHALTUNG_FOLGT}
          ton={hk.status !== 'ok' ? 'neutral' : rueckstand ? 'bad' : 'ok'} wertFarbig={hk.status === 'ok'} />
        <KpiTile layout="kachel" label="Zahlweg" wert={zahlweg(w)} icon={Bank} ton="info" />
      </KpiReihe>

      <Zeilen titel="Zusammensetzung laut Vorschuss je Einheit" zeilen={[
        { label: 'Hausgeldvorschuss (Bewirtschaftung)', wert: eur(z.hausgeld), zahl: true },
        { label: 'Erhaltungsrücklage', wert: eur(z.ruecklage), zahl: true },
        ...einheiten.map(e => ({ label: `${e.bezeichnung} · ${e.objektName}`, wert: <span style={{ color: 'var(--ink2)' }}>{eur(e.hausgeld + e.ruecklage)} · {e.sev ? 'aus SEV-Miete' : 'SEPA'}</span>, zahl: true })),
      ]} />

      {hk.status === 'laedt' && <div className="animate-pulse" style={{ height: 160, borderRadius: 24, background: 'var(--fill2)' }} />}
      {hk.status === 'fehler' && <LeerZustand icon={WarningCircle} titel="Hausgeldkonto nicht geladen" text={hk.meldung} />}
      {hk.status === 'nicht_eingerichtet' && <LeerZustand icon={Coins} titel="Hausgeldeingang und Buchungen" text={BUCHHALTUNG_FOLGT_LANG} />}
      {hk.status === 'ok' && hk.konten.map(k => {
        const e = einheiten.find(x => x.id === k.einheitId);
        if (!k.konto) return <LeerZustand key={k.einheitId} icon={Coins} titel={`Noch keine Buchungen · ${e?.bezeichnung ?? ''}`} text="Für diese Einheit gibt es im Mietkonto noch kein Hausgeld-Soll und keine Zahlung." />;
        const balken = eingangBalken(k.konto);
        const zeilen = buchungsZeilen(k.konto, 'Hausgeld').slice(0, 8);
        return (
          <React.Fragment key={k.einheitId}>
            <InnenKarte style={GLAS}>
              <KartenTitel rechts={einheiten.length > 1 ? `${e?.bezeichnung} · ${e?.objektName}` : undefined}>Hausgeldeingang je Monat</KartenTitel>
              <div className="flex items-end" style={{ gap: 8, height: 130 }}>
                {balken.map(b => (
                  <div key={b.monat} className="flex flex-1 flex-col items-center justify-end h-full" style={{ gap: 6 }}>
                    <div title={b.titel} style={{ width: '100%', maxWidth: 38, height: `${b.hoehe}%`, borderRadius: 9, background: PIP[b.farbe] }} />
                    <span style={{ fontSize: 10, color: 'var(--ink3)' }}>{b.kurz}</span>
                  </div>
                ))}
              </div>
            </InnenKarte>
            <InnenKarte style={GLAS} padding="14px 12px 10px" gap={4}>
              <div style={{ padding: '0 6px 6px' }}><KartenTitel>Hausgeld-Buchungen</KartenTitel></div>
              {zeilen.length === 0 && <span style={{ padding: '6px 8px 10px', fontSize: 13, color: 'var(--ink3)' }}>Noch keine Buchungen</span>}
              {zeilen.map(b => (
                <PanelZeile key={b.key} icon={b.ton === 'bad' ? WarningCircle : b.ton === 'info' ? ArrowDownLeft : CheckCircle} ton={b.ton} titel={b.titel} sub={b.sub} rechts={b.rechts} rechtsFarbe={`var(--${b.ton})`} />
              ))}
            </InnenKarte>
          </React.Fragment>
        );
      })}

      <InnenKarte style={GLAS}>
        <KartenTitel>Hausgeldzahlung erfassen</KartenTitel>
        {einheiten.length > 1 && (
          <SegmentedControl<string> ariaLabel="Einheit" hoehe={34} className="self-start" wert={einheitId} onChange={setEinheitId}
            segmente={einheiten.map(e => ({ key: e.id, label: e.bezeichnung }))} />
        )}
        <FeldRaster minBreite={200}>
          <TextFeld label="Betrag" icon={CurrencyEur} suffix="€" inputMode="decimal" wert={betrag} onChange={setBetrag} disabled={!bereit} placeholder="0,00" />
          <TextFeld label="Eingang am" icon={CalendarBlank} type="date" wert={datum} onChange={setDatum} disabled={!bereit} max={s.heute} />
        </FeldRaster>
        {!bereit && <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Zahlungen lassen sich buchen, sobald die Buchhaltung angebunden ist.</span>}
        <div className="flex flex-wrap justify-end" style={{ gap: 8 }}>
          {erinnerung
            ? <a href={erinnerung} className="immo-hover-flaeche inline-flex items-center" style={{ height: 44, padding: '0 16px', gap: 7, borderRadius: 22, background: 'var(--fill2)', color: 'var(--ink)', fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>
              <BellRinging weight="bold" size={14} />Hausgeld-Erinnerung senden</a>
            : <ImmoButton groesse={44} icon={BellRinging} disabled title={hk.status === 'ok' ? (s.email ? 'Kein Rückstand' : 'Keine E-Mail hinterlegt') : BUCHHALTUNG_FOLGT}>Hausgeld-Erinnerung senden</ImmoButton>}
          {rueckstand && <ImmoButton groesse={44} icon={EnvelopeSimpleOpen} disabled title="Mahnwesen der WEG startet mit der Buchhaltung">An Mahnwesen übergeben</ImmoButton>}
          <ImmoButton art="akzent" groesse={44} icon={Plus} disabled={!bereit || laeuft || !betrag.trim()} onClick={() => { void buche(); }}>
            {laeuft ? 'Wird gebucht …' : 'Zahlung buchen'}
          </ImmoButton>
        </div>
      </InnenKarte>
    </>
  );
}

// ── Sondereigentum (Screen 22) ─────────────────────────────────────────────────
export function SondereigentumPanel() {
  const s = useEigSeite();
  const einheiten = useWegEinheiten(s);
  const objektIds = useMemo(() => [...new Set(einheiten.map(e => e.objektId))], [einheiten]);
  const [toast, zeige] = useToastPill();
  const { unterlagen } = useUnterlagen(s, objektIds, []);
  const mea = einheiten.reduce((a, e) => a + (e.mea ?? 0), 0);
  const teilung = unterlagen.find(u => u.art === 'teilungserklaerung');
  const grundbuch = unterlagen.find(u => u.art === 'grundbuch');
  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <KpiReihe>
        <KpiTile layout="kachel" label="Miteigentumsanteile" wert={`${tausend(mea)} / 1.000`} icon={ChartPieSlice} ton="acc" />
        <KpiTile layout="kachel" label="Sondereigentum" wert={einheiten.length} icon={Door} ton="info" />
        <KpiTile layout="kachel" label="Stimmen (Kopfprinzip)" wert={stimmen(einheiten)} icon={Hand} ton="ok" />
      </KpiReihe>
      <InnenKarte style={GLAS} padding="14px 12px 10px" gap={4}>
        <div style={{ padding: '0 6px 6px' }}><KartenTitel>Einheiten im Sondereigentum</KartenTitel></div>
        {einheiten.map(e => (
          <PanelZeile key={e.id} icon={Key} ton="acc" titel={`${e.bezeichnung} · ${e.objektName}`}
            sub={[e.flaeche ? `${tausend(e.flaeche)} m²` : '', e.sev ? 'vermietet über SEV' : 'selbstgenutzt / eigene Vermietung'].filter(Boolean).join(' · ')}
            rechts={e.mea === null ? '–' : `${tausend(e.mea)}/1.000`} chip={e.sev ? { text: 'SEV', ton: 'acc' } : undefined} />
        ))}
      </InnenKarte>
      <Zeilen titel="Rechtliches" zeilen={[
        { label: 'Teilungserklärung', wert: teilung ? `unter Dokumente (abgelegt ${deDatum(teilung.d.created_at.slice(0, 10))})` : 'nicht unter Dokumente' },
        { label: 'Stimmrecht', wert: 'Kopfprinzip (§ 25 WEG)' },
        { label: 'Grundbuch', wert: grundbuch ? 'Auszug unter Dokumente' : 'nicht unter Dokumente' },
        { label: 'Kostenverteilung', wert: 'nach MEA (§ 16 WEG), sofern nicht anders beschlossen' },
      ]} />
      <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Sondernutzungsrechte sind je Einheit noch nicht hinterlegt.</span>
      <div className="flex flex-wrap" style={{ gap: 8 }}>
        <ImmoButton groesse={44} icon={FileText} disabled={!teilung} title={teilung ? undefined : 'Keine Teilungserklärung unter Dokumente'}
          onClick={() => { if (teilung) void oeffneMieterDokument(teilung.d).then(ok => { if (!ok) zeige('Dokument konnte nicht geöffnet werden', true); }); }}>Teilungserklärung öffnen</ImmoButton>
        <ImmoButton groesse={44} icon={Books} disabled={!grundbuch} title={grundbuch ? undefined : 'Kein Grundbuchauszug unter Dokumente'}
          onClick={() => { if (grundbuch) void oeffneMieterDokument(grundbuch.d).then(ok => { if (!ok) zeige('Dokument konnte nicht geöffnet werden', true); }); }}>Grundbuchauszug</ImmoButton>
      </div>
    </>
  );
}

// ── WEG-Abrechnung & Wirtschaftsplan (Screen 23) ───────────────────────────────
export function WegAbrechnungPanel() {
  const s = useEigSeite();
  const einheiten = useWegEinheiten(s);
  const objektIds = useMemo(() => [...new Set(einheiten.map(e => e.objektId))], [einheiten]);
  const [toast, zeige] = useToastPill();
  const { weg, unterlagen } = useUnterlagen(s, objektIds, []);
  const liste = unterlagen.filter(u => ['einzelabrechnung', 'wirtschaftsplan', 'vermoegensbericht', 'sonderumlage', 'p35a'].includes(u.art)).slice(0, 30);
  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <InnenKarte style={GLAS} padding="14px 12px 10px" gap={4}>
        <div style={{ padding: '0 6px 6px' }}><KartenTitel rechts={liste.length ? `${liste.length} ${liste.length === 1 ? 'Dokument' : 'Dokumente'}` : undefined}>Unterlagen der WEG</KartenTitel></div>
        {weg.status === 'laedt' && <span style={{ padding: '6px 8px 10px', fontSize: 13, color: 'var(--ink3)' }}>Unterlagen werden geladen …</span>}
        {weg.status === 'fehler' && <span style={{ padding: '6px 8px 10px', fontSize: 13, color: 'var(--bad)' }}>Unterlagen nicht geladen: {weg.meldung}</span>}
        {weg.status === 'ok' && liste.length === 0 && <span style={{ padding: '6px 8px 10px', fontSize: 13, color: 'var(--ink3)' }}>Keine Abrechnungen oder Wirtschaftspläne unter Dokumente.</span>}
        {liste.map(u => <DokZeile key={u.d.id} d={u.d} label={u.label} zeige={zeige} />)}
      </InnenKarte>
      <Hinweis titel="Einzelabrechnung und Wirtschaftsplan">Beträge (Nachzahlung, Hausgeld laut Plan, Anteil an Sonderumlagen) erscheinen hier, sobald die WEG-Abrechnung über die Buchhaltung läuft. Bis dahin gelten die abgelegten Dokumente.</Hinweis>
      <div className="flex flex-wrap" style={{ gap: 8 }}>
        <ImmoButton groesse={44} icon={FileText} disabled title="Bereitstellung im Eigentümerportal wird noch angebunden">Im Portal bereitstellen</ImmoButton>
      </div>
    </>
  );
}

// ── Erhaltungsrücklage (Screen 24) ─────────────────────────────────────────────
export function RuecklagePanel() {
  const s = useEigSeite();
  const einheiten = useWegEinheiten(s);
  const objektIds = useMemo(() => [...new Set(einheiten.map(e => e.objektId))], [einheiten]);
  const alleEinheitIds = useMemo(() => s.daten.einheiten.filter(e => e.objekt_id && objektIds.includes(e.objekt_id)).map(e => e.id), [s.daten.einheiten, objektIds]);
  const weg = useWegDaten(objektIds, alleEinheitIds, s.heute);
  const werte = weg.status === 'ok' ? ruecklageWerte(einheiten, weg.daten.einheitEig, s.heute) : [];
  const zuWeg = werte.reduce((a, r) => a + r.zufuehrungWeg, 0);
  const zuEigen = werte.reduce((a, r) => a + r.zufuehrungEigen, 0);
  return (
    <>
      <KpiReihe>
        <KpiTile layout="kachel" label="Rücklage WEG gesamt" wert="–" sub={BUCHHALTUNG_FOLGT} icon={PiggyBank} ton="acc" />
        <KpiTile layout="kachel" label="Zuführung WEG / Jahr" wert={weg.status === 'ok' ? eur(zuWeg, { nachkomma: 0 }) : '–'} icon={TrendUp} ton="info" />
        <KpiTile layout="kachel" label={`Anteil ${s.name} / Jahr`} wert={eur(zuEigen, { nachkomma: 0 })} icon={User} ton="ok" />
      </KpiReihe>
      {werte.length > 1 && (
        <Zeilen titel="Zuführung je WEG" zeilen={werte.map(r => ({ label: `${r.objektName} · MEA ${tausend(r.mea)}`, wert: `${eur(r.zufuehrungEigen, { nachkomma: 0 })} von ${eur(r.zufuehrungWeg, { nachkomma: 0 })} / Jahr`, zahl: true }))} />
      )}
      {weg.status === 'fehler' && <LeerZustand icon={WarningCircle} titel="Rücklage nicht geladen" text={weg.meldung} />}
      <LeerZustand icon={PiggyBank} titel="Entwicklung und Entnahmen" text="Stand der Rücklage, Entwicklung je Jahr und Entnahmen mit Anteil nach MEA erscheinen, sobald die WEG-Konten in der Buchhaltung geführt werden." />
    </>
  );
}

// ── ETV & Beschlüsse (Screen 25) ───────────────────────────────────────────────
export function EtvPanel() {
  const s = useEigSeite();
  const einheiten = useWegEinheiten(s);
  const objektIds = useMemo(() => [...new Set(einheiten.map(e => e.objektId))], [einheiten]);
  const [toast, zeige] = useToastPill();
  const { weg, unterlagen } = useUnterlagen(s, objektIds, []);
  const etv = weg.status === 'ok' ? weg.daten.termine.find(istEtv) ?? null : null;
  const etvObjekt = etv ? einheiten.find(e => e.objektId === etv.objekt_id)?.objektName ?? '' : '';
  const mea = etv ? einheiten.filter(e => e.objektId === etv.objekt_id).reduce((a, e) => a + (e.mea ?? 0), 0) : einheiten.reduce((a, e) => a + (e.mea ?? 0), 0);
  const protokoll = unterlagen.find(u => u.art === 'protokoll');
  const beschluesse = unterlagen.filter(u => u.art === 'beschluss' || u.art === 'protokoll').slice(0, 12);
  // gültige Vertretung für die Versammlung: ETV-Vollmacht vor General-/Vorsorgevollmacht (Miteigentümer & Vertreter)
  const stichtag = etv ? etv.start_datum.slice(0, 10) : s.heute;
  const etvVollmacht = ['etv_vollmacht', 'generalvollmacht', 'vorsorgevollmacht', 'betreuer']
    .map(art => s.daten.beteiligte.find(b => b.typ === 'vertreter' && b.art === art && vollmachtGueltig(b, stichtag)))
    .find(Boolean) ?? null;
  const vollmacht = s.email
    ? `mailto:${s.email}?subject=${encodeURIComponent(`Vollmacht zur Eigentümerversammlung${etvObjekt ? ` ${etvObjekt}` : ''}`)}&body=${encodeURIComponent(`Guten Tag,\n\nfalls Sie an der Eigentümerversammlung${etv ? ` am ${deDatum(etv.start_datum.slice(0, 10))}` : ''} nicht teilnehmen können, senden Sie uns bitte eine Vollmacht für Ihre Vertretung zu.\n\nMit freundlichen Grüßen`)}`
    : null;
  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      {etv ? (
        <Zeilen titel="Nächste Eigentümerversammlung" zeilen={[
          { label: 'Objekt', wert: etvObjekt || '–' },
          { label: 'Termin', wert: `${deDatum(etv.start_datum.slice(0, 10))}${etv.start_datum.length > 10 ? `, ${etv.start_datum.slice(11, 16)} Uhr` : ''}`, zahl: true },
          { label: 'Titel', wert: etv.titel || 'Eigentümerversammlung' },
          { label: 'Einladung', wert: `spätestens ${deDatum(einladungBis(etv.start_datum))} (3 Wochen, § 24 WEG)` },
          { label: `MEA ${s.name}`, wert: `${tausend(mea)} / 1.000`, zahl: true },
          { label: 'Vollmacht', wert: etvVollmacht
            ? `${etvVollmacht.name} (${artLabel(etvVollmacht.art)}${etvVollmacht.gueltig_bis ? ` bis ${deDatum(etvVollmacht.gueltig_bis)}` : ''})`
            : <span style={{ color: 'var(--ink3)' }}>nicht hinterlegt</span> },
        ]} />
      ) : (
        <LeerZustand icon={CalendarBlank} titel="Keine Eigentümerversammlung geplant"
          text={weg.status === 'laedt' ? 'Termine werden geladen …' : 'Im Kalender ist für die WEG kein ETV-Termin eingetragen (Titel „ETV“ oder „Eigentümerversammlung“).'} />
      )}
      <InnenKarte style={GLAS} padding="14px 12px 10px" gap={4}>
        <div style={{ padding: '0 6px 6px' }}><KartenTitel>Beschlüsse &amp; Protokolle</KartenTitel></div>
        {beschluesse.length === 0 && <span style={{ padding: '6px 8px 10px', fontSize: 13, color: 'var(--ink3)' }}>Keine Protokolle oder Beschlüsse unter Dokumente. Eine Beschlusssammlung mit Status wird noch angebunden.</span>}
        {beschluesse.map(u => <DokZeile key={u.d.id} d={u.d} label={u.label} zeige={zeige} />)}
      </InnenKarte>
      <div className="flex flex-wrap" style={{ gap: 8 }}>
        {vollmacht
          ? <a href={vollmacht} className="immo-hover-flaeche inline-flex items-center" style={{ height: 44, padding: '0 16px', gap: 7, borderRadius: 22, background: 'var(--acc-fill)', color: 'var(--acc-ink)', fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>
            <Signature weight="bold" size={14} />Vollmacht anfordern</a>
          : <ImmoButton groesse={44} icon={Signature} disabled title="Keine E-Mail hinterlegt">Vollmacht anfordern</ImmoButton>}
        <ImmoButton groesse={44} icon={FileText} disabled={!protokoll} title={protokoll ? undefined : 'Kein Protokoll unter Dokumente'}
          onClick={() => { if (protokoll) void oeffneMieterDokument(protokoll.d).then(ok => { if (!ok) zeige('Dokument konnte nicht geöffnet werden', true); }); }}>
          {protokoll ? `Protokoll ${deDatum(protokoll.d.created_at.slice(0, 10)).slice(-4)}` : 'Protokoll'}
        </ImmoButton>
        <ImmoButton groesse={44} icon={Books} disabled title="Beschlusssammlung wird noch angebunden">Beschlusssammlung</ImmoButton>
      </div>
    </>
  );
}

// ── Verwalter der WEG (Screen 26) ──────────────────────────────────────────────
export function WegVerwalterPanel() {
  const s = useEigSeite();
  const einheiten = useWegEinheiten(s);
  const wegs = [...new Map(einheiten.map(e => [e.objektId, e.objektName])).entries()];
  return (
    <>
      {wegs.map(([objektId, name]) => <Bestellung key={objektId} objektId={objektId} name={name} mehrere={wegs.length > 1} />)}
      <Hinweis titel="Hinweis">
        Mit {s.name} als WEG-Eigentümer besteht kein eigener Verwaltervertrag. Vertragspartner ist die Gemeinschaft der Wohnungseigentümer.
        {s.daten.zeile.werte.hasSEV ? ' Für die Vermietung gibt es zusätzlich einen SEV-Vertrag (siehe Verwaltervertrag).' : ''}
      </Hinweis>
    </>
  );
}

/** Verwalterbestellung einer WEG – gespeichert am Objekt (gilt für alle Eigentümer der WEG). */
function Bestellung({ objektId, name, mehrere }: { objektId: string; name: string; mehrere: boolean }) {
  const s = useEigSeite();
  const o = s.daten.objekte.find(x => x.id === objektId);
  const txt = (v: string | null | undefined) => (v ?? '').trim();
  const start = { seit: txt(o?.weg_bestellt_seit).slice(0, 10), bis: txt(o?.weg_bestellt_bis).slice(0, 10), beirat: txt(o?.weg_beirat) };
  const [seit, setSeit] = useState(start.seit);
  const [bis, setBis] = useState(start.bis);
  const [beirat, setBeirat] = useState(start.beirat);
  const [offen, setOffen] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [toast, zeige] = useToastPill();
  const fehler = bestellungFehler(seit, bis);
  const status = bestellungStatus(start.bis || null, s.heute);
  const verwalter = VERWALTUNG_ABSENDER[0] ?? 'Verwaltung';
  const geaendert = seit !== start.seit || bis !== start.bis || beirat.trim() !== start.beirat;

  const speichere = async () => {
    if (fehler) { zeige(fehler, true); return; }
    setLaeuft(true);
    try {
      await aktualisiereObjekt(objektId, { weg_bestellt_seit: seit || null, weg_bestellt_bis: bis || null, weg_beirat: beirat.trim() || null });
      zeige(`Verwalterbestellung ${name} gespeichert`);
      setOffen(false);
      meldeEigentuemerGeaendert(s.daten.eigentuemer.id);
    } catch (err) {
      zeige(`Nicht gespeichert: ${fehlerText(err)}`, true);
    } finally {
      setLaeuft(false);
    }
  };

  const grau = (t: string) => <span style={{ color: 'var(--ink3)' }}>{t}</span>;
  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <Zeilen titel={mehrere ? `Verwalterbestellung · ${name}` : 'Verwalterbestellung'} zeilen={[
        { label: 'Verwalter', wert: verwalter },
        { label: 'Vertragspartner', wert: `GdWE ${name}` },
        { label: 'Bestellt seit', wert: start.seit ? deDatum(start.seit) : grau('nicht hinterlegt'), zahl: true },
        { label: 'Bestellt bis', wert: start.bis
          ? <span style={{ color: status ? (status === 'abgelaufen' ? 'var(--bad)' : 'var(--warn)') : 'var(--ink)' }}>{deDatum(start.bis)} (max. 5 Jahre, § 26 WEG)</span>
          : grau('nicht hinterlegt (max. 5 Jahre, § 26 WEG)'), zahl: true },
        { label: 'Vergütung', wert: 'im Hausgeld enthalten' },
        { label: 'Verwaltungsbeirat', wert: start.beirat || grau('nicht hinterlegt') },
      ]} />
      {status && (
        <Hinweis titel={status === 'abgelaufen' ? 'Bestellung abgelaufen' : 'Wiederbestellung vorbereiten'}>
          {status === 'abgelaufen' ? `Die Bestellung endete am ${deDatum(start.bis)}. Ohne Wiederbestellung durch die ETV fehlt die Grundlage für die Verwaltung.` : `Die Bestellung endet am ${deDatum(start.bis)}. Die Wiederbestellung sollte auf der nächsten ETV beschlossen werden.`}
        </Hinweis>
      )}
      {offen && (
        <InnenKarte style={{ ...GLAS, boxShadow: 'inset 0 0 0 2px var(--acc)' }}>
          <KartenTitel icon={Gavel}>Verwalterbestellung bearbeiten</KartenTitel>
          <FeldRaster minBreite={200}>
            <TextFeld label="Bestellt seit" type="date" icon={CalendarBlank} wert={seit} onChange={setSeit} />
            <TextFeld label="Bestellt bis" type="date" icon={CalendarBlank} wert={bis} onChange={setBis} min={seit || undefined} fehler={fehler ?? undefined} />
            <TextFeld label="Verwaltungsbeirat" icon={Users} wert={beirat} onChange={setBeirat} placeholder="z. B. Thomas Berg (Vorsitz), Anna Keller" voll />
          </FeldRaster>
          <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Gilt für alle Eigentümer der WEG {name}.</span>
          <div className="flex justify-end flex-wrap" style={{ gap: 8 }}>
            <ImmoButton onClick={() => { setOffen(false); setSeit(start.seit); setBis(start.bis); setBeirat(start.beirat); }}>Abbrechen</ImmoButton>
            <ImmoButton art="akzent" icon={Check} disabled={laeuft || !geaendert || !!fehler} onClick={() => { void speichere(); }}>{laeuft ? 'Wird gespeichert …' : 'Speichern'}</ImmoButton>
          </div>
        </InnenKarte>
      )}
      <div className="flex flex-wrap" style={{ gap: 8 }}>
        <ImmoButton art="akzent" groesse={44} icon={Buildings} onClick={() => s.zumObjekt(objektId)}>Zum Objekt</ImmoButton>
        {!offen && <ImmoButton groesse={44} icon={PencilSimple} onClick={() => setOffen(true)}>Bestellung bearbeiten</ImmoButton>}
        <ImmoButton groesse={44} icon={Gavel} onClick={() => s.oeffnePanel('etv')}>Beschlüsse &amp; Protokolle</ImmoButton>
      </div>
    </>
  );
}
